<template>
  <Stack>
    <Stack space="xsmall">
      <Columns :space="['xsmall', 'small']" collapseBelow="lg" align-y="bottom">
        <Column width="1/5">
          <FormTextInput
            id="street_number"
            name="street_number"
            label="Street #"
            :error="errors?.street_number?.[0]"
            :errorIsTooltip="true"
            :value="address?.street_number"
            :isDisabled="isDisabled"
            @update:modelValue="handleAddressUpdate"
          />
        </Column>
        <Column width="fluid">
          <FormTextInput
            id="street_name"
            name="street_name"
            label="Street Name"
            :error="errors?.street_name?.[0]"
            :errorIsTooltip="true"
            :value="address?.street_name"
            :isDisabled="isDisabled"
            @update:modelValue="handleAddressUpdate"
          />
        </Column>
        <Column width="1/4">
          <FormTextInput
            delay="250"
            id="unit_number"
            name="unit_number"
            label="Unit #"
            placeholderText="Optional"
            :error="errors?.unit_number?.[0]"
            :errorIsTooltip="true"
            :value="address?.unit_number"
            :isDisabled="isDisabled"
            @update:modelValue="handleAddressUpdate"
          />
        </Column>
      </Columns>
      <Columns :space="['xsmall', 'small']" collapseBelow="lg" align-y="bottom">
        <Column width="1/2">
          <FormTextInput
            id="city"
            name="city"
            label="City"
            :error="errors?.city?.[0]"
            :errorIsTooltip="true"
            :value="address?.city || address?.city_region"
            :isDisabled="isDisabled"
            @update:modelValue="handleAddressUpdate"
          />
        </Column>
        <Column width="1/4">
          <FormTextInput
            id="state_or_province"
            name="state_or_province"
            label="State"
            :error="errors?.state_or_province?.[0]"
            :errorIsTooltip="true"
            :value="address?.state_or_province"
            :isDisabled="isDisabled"
            @update:modelValue="handleAddressUpdate"
          />
        </Column>
        <Column width="1/4">
          <FormTextInput
            id="postal_code"
            name="postal_code"
            label="Postal Code"
            :error="errors?.postal_code?.[0]"
            :errorIsTooltip="true"
            :value="address?.postal_code"
            :isDisabled="isDisabled"
            @update:modelValue="handleAddressUpdate"
          />
        </Column>
      </Columns>

      <FormSearchInput
        v-if="hasCountryInput"
        id="country"
        label="Country"
        name="country"
        :error="errors?.country?.[0]"
        :value="address?.country"
        @update:modelValue="handleCountrySearch"
      >
        <Stack as="ul" itemAs="li" :showDividers="true" space="none">
          <ListItem
            v-for="country in countries"
            :isActionable="true"
            :key="country.code"
            :title="country.name"
            :subtitle="country.code"
            :value="country.code"
            @click="handleAddressUpdate({ key: 'country', value: country.code })"
          />
        </Stack>
      </FormSearchInput>

      <FormField
        v-if="hasMap"
        :error="customMapError"
        label="Location"
        helperText="Moving the pin on the map will update the Latitude and Longitude for this address"
      >
        <AddressMap
          hasDraggableMarker
          :latitude="address?.latitude"
          :longitude="address?.longitude"
          :mapErrorMessage="customMapError"
          @onChange="handleAddressUpdate"
        />
      </FormField>
    </Stack>
  </Stack>
</template>

<script setup>
import { computed, ref } from 'vue'
import {
  Column,
  Columns,
  FormField,
  Stack,
  FormTextInput,
  FormSearchInput,
  ListItem,
} from '@aryeo/runway'

import AddressMap from './AddressMap.vue'
import RegionApi from '@/Services/Api/Company/V1/RegionApi'

const props = defineProps({
  address: {
    type: Object,
    default: () => ({}),
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
  hasMap: {
    type: Boolean,
    default: true,
  },
  hasCountryInput: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['onChange'])

const countries = ref()

const customMapError = computed(() => {
  if (
    props.errors?.unparsed_address &&
    props.errors?.unparsed_address?.[0]?.includes(
      'The unparsed address field is required when none of latitude / longitude are present.'
    )
  ) {
    return 'Please enter your address details and place the pin on the map at the property location.'
  }

  return [props.errors?.latitude?.[0], props.errors?.latitude?.[0]].filter((v) => v).join(' / ')
})

const handleAddressUpdate = ({ key, value }) => {
  emit('onChange', {
    key,
    value,
  })
}

const handleCountrySearch = async ({ key, value }) => {
  if (key === 'country') {
    const results = await RegionApi.findCountry(value).then((res) => res.data)
    countries.value = results.data
  }
}
</script>
